import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ContextRuleView } from "@bitacorapro/api-types";
import { ContextConflictDrawer, groupConflicts } from "../components/ContextConflictDrawer";
import { ContextRuleEditDrawer } from "../components/ContextRuleEditDrawer";
import { EmptyState } from "../components/EmptyState";
import { QueryBoundary } from "../components/QueryBoundary";
import { SkelTable } from "../components/skeleton/SkelTable";
import { kindLabel } from "../features/context/contextUpload";
import {
  useAcceptContextRule,
  useContextRules,
  useContextSource,
  useDiscardContextRule,
  useRecipe,
} from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { canManageContextSources } from "../lib/capabilities";
import { ContextSourceStatusPill } from "./ContextoMarca";

// /contexto/fuentes/:id/reglas — the review table (ADR-0173 fase 2, PR-7).
// One row per rule, current version; filters by what needs a person;
// edit (v+1), accept (strict on slot conflicts), discard, resolve a
// conflict group. «Lista para activar» = 0 in conflict, 0 flagged drafts
// — the detail screen carries the Activar button.

type Filter = "todas" | "conflicto" | "marcadas" | "descartadas";

const STATUS_LABEL: Record<ContextRuleView["status"], string> = {
  draft: "Borrador",
  edited: "Editada",
  accepted: "Aceptada",
  discarded: "Descartada",
  in_conflict: "En conflicto",
  superseded: "Reemplazada",
};
const STATUS_TONE: Record<ContextRuleView["status"], "ok" | "warn" | "crit" | "idle"> = {
  draft: "idle",
  edited: "ok",
  accepted: "ok",
  discarded: "idle",
  in_conflict: "crit",
  superseded: "idle",
};
const FLAG_LABEL: Record<string, string> = {
  ambiguo: "Ambigua",
  responsabilidad_en_conflicto: "Responsable dudoso",
  posible_conflicto: "Posible conflicto",
  hoja_oculta: "De hoja oculta",
  texto_cortado: "Texto cortado",
  requiere_revision: "Revisar",
};
const VERIFICATION_LABEL = { foto: "Foto", dato: "Dato", humano: "Humano" } as const;

function needsPerson(r: ContextRuleView): boolean {
  return r.status === "draft" && r.version.flags.includes("requiere_revision");
}

export function ContextoRevision() {
  const { id } = useParams<{ id: string }>();
  const session = useSession();
  const canManage = canManageContextSources(session?.organization.role);
  const { data: recipe } = useRecipe();
  const source = useContextSource(id);
  const rules = useContextRules(id, !!id);
  const accept = useAcceptContextRule();
  const discard = useDiscardContextRule();
  const [filter, setFilter] = useState<Filter>("todas");
  const [editing, setEditing] = useState<ContextRuleView | null>(null);
  const [resolving, setResolving] = useState<ContextRuleView[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const groups = useMemo(() => groupConflicts(rules.data?.rules ?? []), [rules.data]);
  const reviewable = source.data?.status === "in_review" || source.data?.status === "active";

  async function run(action: () => Promise<unknown>, done: string) {
    setActionError(null);
    try {
      await action();
      setToast(done);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "La acción falló.");
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to="/contexto">Contexto del cliente</Link>
            {source.data && (
              <>
                {" · "}
                <Link to={`/contexto/fuentes/${source.data.id}`}>
                  {source.data.fileName ?? kindLabel(recipe, source.data.kind)}
                </Link>
              </>
            )}
          </div>
          <h1 className="page-header__title">Revisión de reglas</h1>
          {source.data && (
            <p className="page-header__sub">
              <ContextSourceStatusPill source={source.data} />
              {rules.data && (
                <>
                  {" · "}
                  {rules.data.counts.total} reglas · {rules.data.counts.inConflict} en conflicto ·{" "}
                  {rules.data.counts.needingReview} por revisar
                  {rules.data.counts.inConflict === 0 && rules.data.counts.needingReview === 0
                    ? " · lista para activar"
                    : ""}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="stack-gap">
        <div className="filter-row" role="group" aria-label="Filtrar reglas">
          {(
            [
              ["todas", "Todas"],
              ["conflicto", "En conflicto"],
              ["marcadas", "Por revisar"],
              ["descartadas", "Descartadas"],
            ] as Array<[Filter, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`filter-chip${filter === key ? " filter-chip--active" : ""}`}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              <span className="filter-chip__label">{label}</span>
            </button>
          ))}
        </div>

        {actionError && (
          <div className="empty" role="alert">
            <div className="empty__title">{actionError}</div>
          </div>
        )}

        <div className="panel">
          <QueryBoundary
            query={rules}
            label="las reglas"
            errorTitle="No pudimos cargar las reglas."
            kind="section"
            skeleton={
              <SkelTable
                rows={5}
                cols={6}
                headers={["Ítems", "Regla", "Verificación", "Responsable", "Estado", ""]}
              />
            }
            isEmpty={(d) => d.rules.length === 0}
            empty={
              <EmptyState
                title="El extractor no produjo reglas para este documento."
                description="Revisa que el documento contenga estándares verificables, o súbelo de nuevo tras corregirlo."
              />
            }
          >
            {(data) => {
              const visible = data.rules.filter((r) => {
                if (filter === "conflicto") return r.status === "in_conflict";
                if (filter === "marcadas") return needsPerson(r);
                if (filter === "descartadas") return r.status === "discarded";
                return true;
              });
              return (
                <div className="panel__body--flush">
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th>Ítems</th>
                        <th>Regla</th>
                        <th>Verificación</th>
                        <th>Responsable</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((r) => {
                        const group = groups.find((g) => g.includes(r));
                        const closed = r.status === "discarded" || r.status === "superseded";
                        return (
                          <tr key={r.id}>
                            <td className="cell-meta">{r.itemCodes.join(", ") || "—"}</td>
                            <td>
                              <span className="cell-strong">{r.version.text}</span>
                              {r.version.flags.length > 0 && (
                                <div className="cell-meta">
                                  {r.version.flags.map((f) => FLAG_LABEL[f] ?? f).join(" · ")}
                                </div>
                              )}
                              {r.version.params.length > 0 && (
                                <div className="cell-meta">
                                  {r.version.params
                                    .map(
                                      (p) =>
                                        `${p.name} ${p.op} ${Array.isArray(p.value) ? p.value.join("→") : String(p.value)}${p.unit ? ` ${p.unit}` : ""}${p.scope ? ` (${p.scope})` : ""}`
                                    )
                                    .join(" · ")}
                                </div>
                              )}
                              {r.version.version > 1 && (
                                <div className="cell-meta">v{r.version.version}</div>
                              )}
                            </td>
                            <td className="cell-meta">
                              {VERIFICATION_LABEL[r.version.verification]}
                            </td>
                            <td className="cell-meta">
                              {r.version.responsible === "no_especificado"
                                ? "—"
                                : r.version.responsible}
                            </td>
                            <td>
                              <span className={`st-pill st-pill--${STATUS_TONE[r.status]}`}>
                                <span className={`st-dot st-dot--${STATUS_TONE[r.status]}`} />
                                {STATUS_LABEL[r.status]}
                              </span>
                            </td>
                            <td className="cell-actions">
                              {canManage && reviewable && !closed && (
                                <>
                                  {r.status === "in_conflict" && group ? (
                                    <button
                                      type="button"
                                      className="btn btn--secondary btn--sm"
                                      onClick={() => setResolving(group)}
                                    >
                                      Resolver
                                    </button>
                                  ) : (
                                    r.status !== "accepted" && (
                                      <button
                                        type="button"
                                        className="btn btn--secondary btn--sm"
                                        disabled={accept.isPending}
                                        onClick={() =>
                                          void run(
                                            () => accept.mutateAsync(r.id),
                                            "Regla aceptada."
                                          )
                                        }
                                      >
                                        Aceptar
                                      </button>
                                    )
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    onClick={() => setEditing(r)}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn--ghost btn--sm"
                                    disabled={discard.isPending}
                                    onClick={() =>
                                      void run(() => discard.mutateAsync(r.id), "Regla descartada.")
                                    }
                                  >
                                    Descartar
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }}
          </QueryBoundary>
        </div>
      </div>

      {editing && (
        <ContextRuleEditDrawer
          rule={editing}
          recipe={recipe}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setToast("Regla guardada como nueva versión.");
          }}
        />
      )}
      {resolving && (
        <ContextConflictDrawer
          group={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            setToast("Conflicto resuelto.");
          }}
        />
      )}
      {toast && (
        <div className="toast-zone">
          <div className="toast toast--ok" role="status" aria-live="polite">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
